// Baselayer Background Checks — controlled UCC filing draft editor (filing
// type/state, debtor block, secured-party block, collateral). The parent owns
// the value + persistence; this is a pure form surface.

import { Input, Label } from "@/design-system/components/ui";
import type {
  BgcUccFilingDraftRequest,
  BgcUccFilingType,
} from "@/api/backgroundChecks/bgcUccFilingApi";
import { UCC_FILING_TYPE_LABELS } from "./checkFormat";

interface UccFilingEditorProps {
  value: BgcUccFilingDraftRequest;
  onChange: (next: BgcUccFilingDraftRequest) => void;
  disabled?: boolean;
}

const FILING_TYPES = Object.keys(UCC_FILING_TYPE_LABELS) as BgcUccFilingType[];

const selectClass =
  "px-space-3 py-[10px] border border-border-light rounded-md bg-white-pure text-small text-onyx " +
  "focus:outline-none focus:border-gold focus:shadow-goldRing disabled:bg-white-warm disabled:opacity-60";

export function UccFilingEditor({ value, onChange, disabled }: UccFilingEditorProps) {
  const set = <K extends keyof BgcUccFilingDraftRequest>(
    key: K,
    next: BgcUccFilingDraftRequest[K],
  ) => onChange({ ...value, [key]: next });

  const text = (key: keyof BgcUccFilingDraftRequest): string =>
    (value[key] as string | null | undefined) ?? "";

  return (
    <div className="flex flex-col gap-space-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-space-3">
        <div className="flex flex-col gap-space-1">
          <Label htmlFor="ucc-filing-type">Filing type</Label>
          <select
            id="ucc-filing-type"
            className={selectClass}
            value={value.filingType}
            onChange={(e) => set("filingType", e.target.value as BgcUccFilingType)}
            disabled={disabled}
          >
            {FILING_TYPES.map((t) => (
              <option key={t} value={t}>
                {UCC_FILING_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </div>
        <Field
          id="ucc-filing-state"
          label="Filing state"
          value={text("filingState")}
          onChange={(v) => set("filingState", v)}
          disabled={disabled}
        />
        <fieldset className="flex flex-col gap-space-1 border-0 p-0 m-0 sm:col-span-2">
          <legend className="p-0 font-display text-label font-semibold uppercase tracking-label text-gray-mid mb-[6px]">
            Term
          </legend>
          <div className="flex items-center gap-space-4">
            <label className="flex items-center gap-space-2 text-small text-onyx">
              <input
                type="radio"
                name="ucc-term"
                checked={value.isLongTerm !== true}
                onChange={() => set("isLongTerm", false)}
                disabled={disabled}
                className="h-4 w-4 accent-gold"
                data-testid="ucc-term-standard"
              />
              Standard (5-year)
            </label>
            <label className="flex items-center gap-space-2 text-small text-onyx">
              <input
                type="radio"
                name="ucc-term"
                checked={value.isLongTerm === true}
                onChange={() => set("isLongTerm", true)}
                disabled={disabled}
                className="h-4 w-4 accent-gold"
                data-testid="ucc-term-long"
              />
              Long-term (30-year)
            </label>
          </div>
        </fieldset>
      </div>

      <Section title="Debtor">
        <Field
          id="ucc-debtor-name"
          label="Legal name"
          required
          value={value.debtorLegalName}
          onChange={(v) => set("debtorLegalName", v)}
          disabled={disabled}
        />
        <Field
          id="ucc-debtor-type"
          label="Type"
          value={text("debtorType")}
          onChange={(v) => set("debtorType", v)}
          disabled={disabled}
        />
        <Field
          id="ucc-debtor-line1"
          label="Mailing address"
          value={text("debtorMailingLine1")}
          onChange={(v) => set("debtorMailingLine1", v)}
          disabled={disabled}
        />
        <Field
          id="ucc-debtor-city"
          label="City"
          value={text("debtorCity")}
          onChange={(v) => set("debtorCity", v)}
          disabled={disabled}
        />
        <Field
          id="ucc-debtor-state"
          label="State"
          value={text("debtorState")}
          onChange={(v) => set("debtorState", v)}
          disabled={disabled}
        />
        <Field
          id="ucc-debtor-zip"
          label="ZIP"
          value={text("debtorZip")}
          onChange={(v) => set("debtorZip", v)}
          disabled={disabled}
        />
        <Field
          id="ucc-debtor-jurisdiction"
          label="Jurisdiction of org"
          value={text("debtorJurisdictionOfOrg")}
          onChange={(v) => set("debtorJurisdictionOfOrg", v)}
          disabled={disabled}
        />
      </Section>

      <Section title="Secured party">
        <Field
          id="ucc-sp-name"
          label="Legal name"
          required
          value={value.securedPartyLegalName}
          onChange={(v) => set("securedPartyLegalName", v)}
          disabled={disabled}
        />
        <Field
          id="ucc-sp-type"
          label="Type"
          value={text("securedPartyType")}
          onChange={(v) => set("securedPartyType", v)}
          disabled={disabled}
        />
        <Field
          id="ucc-sp-line1"
          label="Mailing address"
          value={text("securedPartyMailingLine1")}
          onChange={(v) => set("securedPartyMailingLine1", v)}
          disabled={disabled}
        />
        <Field
          id="ucc-sp-city"
          label="City"
          value={text("securedPartyCity")}
          onChange={(v) => set("securedPartyCity", v)}
          disabled={disabled}
        />
        <Field
          id="ucc-sp-state"
          label="State"
          value={text("securedPartyState")}
          onChange={(v) => set("securedPartyState", v)}
          disabled={disabled}
        />
        <Field
          id="ucc-sp-zip"
          label="ZIP"
          value={text("securedPartyZip")}
          onChange={(v) => set("securedPartyZip", v)}
          disabled={disabled}
        />
      </Section>

      <Section title="Collateral">
        <Field
          id="ucc-collateral-type"
          label="Description type"
          value={text("collateralDescriptionType")}
          onChange={(v) => set("collateralDescriptionType", v)}
          disabled={disabled}
        />
        <div className="flex flex-col gap-space-1 sm:col-span-2">
          <Label htmlFor="ucc-collateral-text">Description</Label>
          <textarea
            id="ucc-collateral-text"
            rows={4}
            className="w-full px-space-3 py-[10px] font-body text-small text-onyx bg-white-pure leading-normal border border-border-light rounded-md placeholder:text-gray-mid focus:outline-none focus:border-gold focus:shadow-goldRing resize-y disabled:bg-white-warm disabled:opacity-60"
            value={text("collateralDescriptionText")}
            onChange={(e) => set("collateralDescriptionText", e.target.value)}
            disabled={disabled}
          />
        </div>
      </Section>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <fieldset className="border border-border-light rounded-md p-space-4">
      <legend className="font-display text-label font-semibold uppercase tracking-label text-gray-mid px-space-1">
        {title}
      </legend>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-space-3">{children}</div>
    </fieldset>
  );
}

interface FieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (next: string) => void;
  required?: boolean;
  disabled?: boolean;
}

function Field({ id, label, value, onChange, required, disabled }: FieldProps) {
  return (
    <div className="flex flex-col gap-space-1">
      <Label htmlFor={id} required={required}>
        {label}
      </Label>
      <Input
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      />
    </div>
  );
}
